import { useEffect, useState } from "react";
import { api } from "@/lib/apiClient";
import { Trophy, TrendingUp, ShieldCheck, Award, Calendar } from "lucide-react";
import { mbtiInfo, clubInfo } from "@/lib/mbti";

/**
 * Coach Champions panel — Secretário da Semana + Secretário do Mês.
 * Visible only to Secretário & Admin. Uses the d) Combined formula:
 *   40% avg + 30% growth + 30% discipline (presença+reporting)
 */
export default function CoachChampions() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get("/coach-champions").then((r) => setData(r.data)).catch((e) => {
      setErr(e.response?.status === 403 ? "Esta área é apenas visível a Secretário e Admin." : "Não foi possível carregar.");
    });
  }, []);

  if (err) return <div className="kw-card p-4 text-xs text-[#737373]" data-testid="coach-champions-err">{err}</div>;
  if (!data) return <div className="kw-card p-4 text-xs text-[#737373]" data-testid="coach-champions-loading">A carregar campeonato dos secretários…</div>;

  const w = data.weekly;
  const m = data.monthly;

  return (
    <div className="space-y-6" data-testid="coach-champions">
      <div className="flex items-center gap-3">
        <Trophy className="w-7 h-7 text-[#EAB308]" />
        <div>
          <div className="kw-overline mb-1 text-[#EAB308]">Apenas Secretário &amp; Admin</div>
          <h2 className="font-display text-3xl md:text-4xl font-black uppercase tracking-tight">
            Campeonato dos <span className="text-[#EAB308]">Secretários</span>
          </h2>
          <div className="text-[10px] text-[#737373] uppercase tracking-widest mt-1">
            Critério combinado: 40% média · 30% crescimento · 30% disciplina (presença+reporting)
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <ChampionCard
          icon={<Calendar className="w-5 h-5 text-[#EAB308]" />}
          title="Secretário da Semana"
          subtitle={`Semana ${w.week_number}`}
          rows={w.rows}
          winner={w.winner}
          testid="coach-of-the-week"
        />
        <ChampionCard
          icon={<Award className="w-5 h-5 text-[#EAB308]" />}
          title="Secretário do Mês"
          subtitle={`${m.month_label} · ${m.weeks_included.length} semana${m.weeks_included.length === 1 ? "" : "s"}`}
          rows={m.rows}
          winner={m.winner}
          testid="coach-of-the-month"
        />
      </div>

      {data.week_history && data.week_history.length > 1 && (
        <div className="kw-card p-5" data-testid="coach-week-history">
          <div className="kw-overline mb-3">Histórico semanal</div>
          <div className="space-y-2">
            {data.week_history.map((wh) => (
              <div key={wh.week} className="flex items-center justify-between border-b border-[#1A1A1A] pb-1.5 text-sm">
                <div className="kw-overline text-[#737373]">Semana {wh.week}</div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-[#A3A3A3]">{wh.winner?.coach?.name || wh.winner?.club || "—"}</span>
                  <span className="font-display text-sm font-black text-[#EAB308]">{wh.winner?.club}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ChampionCard({ icon, title, subtitle, rows, winner, testid }) {
  return (
    <div className="kw-card p-6 border-[#EAB308]/40 relative overflow-hidden" data-testid={testid}>
      <div className="absolute top-0 right-0 w-32 h-32 bg-[#EAB308]/5 rounded-full -mr-16 -mt-16 pointer-events-none" />
      <div className="flex items-center justify-between mb-4 border-b border-[#222] pb-3">
        <div className="flex items-center gap-2">
          {icon}
          <div>
            <div className="kw-overline">{title}</div>
            <div className="text-[10px] text-[#737373] uppercase tracking-widest">{subtitle}</div>
          </div>
        </div>
        <Trophy className="w-7 h-7 text-[#EAB308]" />
      </div>

      {winner && winner.coach ? (
        <div className="space-y-3 relative">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-[#EAB308] text-black flex items-center justify-center">
              <Trophy className="w-6 h-6" />
            </div>
            <div className="flex-1">
              <div className="font-display text-xl font-black uppercase tracking-tight">{winner.coach.name}</div>
              <div className="text-[10px] text-[#EAB308] uppercase tracking-widest">{winner.club}</div>
              {winner.coach.coach_mbti && (() => {
                const info = mbtiInfo(winner.coach.coach_mbti) || (winner.club ? clubInfo(winner.club) : null);
                return (
                  <div
                    className="mt-1.5 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 border"
                    style={{ color: info?.color, borderColor: info?.border, background: info?.bg }}
                    data-testid={`${testid}-mbti`}>
                    <span className="font-mono">{winner.coach.coach_mbti}</span>
                    {winner.coach.coach_personality_label && (
                      <span className="normal-case tracking-normal">· {winner.coach.coach_personality_label}</span>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
          <ScoreLine label="Média de pts" value={winner.avg_per_player} icon={<TrendingUp className="w-3 h-3" />} />
          <ScoreLine label="Crescimento" value={winner.growth} icon={<TrendingUp className="w-3 h-3 rotate-45" />} />
          <ScoreLine label="Disciplina" value={`${winner.discipline_pct}%`} icon={<ShieldCheck className="w-3 h-3" />} />
          <div className="pt-2 mt-2 border-t border-[#222] flex items-baseline justify-between">
            <div className="kw-overline">Score combinado</div>
            <div className="font-display text-2xl font-black text-[#EAB308]">{(winner.score * 100).toFixed(1)}<span className="text-sm text-[#737373]">/100</span></div>
          </div>
        </div>
      ) : (
        <div className="text-xs text-[#737373] italic py-4">Sem dados suficientes ainda.</div>
      )}

      {rows.length > 1 && (
        <div className="mt-4 pt-3 border-t border-[#222] space-y-1.5">
          <div className="kw-overline mb-2">Classificação</div>
          {rows.map((r, i) => (
            <div key={r.club} className="flex items-center justify-between text-xs" data-testid={`${testid}-rank-${i}`}>
              <div className="flex items-center gap-2">
                <span className={`font-display font-black w-5 text-center ${i === 0 ? "text-[#EAB308]" : "text-[#737373]"}`}>
                  {i + 1}
                </span>
                <span>{r.coach?.name || "Sem secretário"}</span>
                <span className="text-[#737373]">({r.club})</span>
              </div>
              <span className={`font-display font-bold ${i === 0 ? "text-[#EAB308]" : "text-[#A3A3A3]"}`}>
                {(r.score * 100).toFixed(0)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ScoreLine({ label, value, icon }) {
  return (
    <div className="flex items-center justify-between text-xs">
      <span className="text-[#737373] flex items-center gap-1.5">{icon}{label}</span>
      <span className="font-display font-bold">{value}</span>
    </div>
  );
}
